import * as THREE from 'three'
import type {
  TopologyFeatureFaceGeometryProvider,
  TopologyFeatureFaceGeometryProviderInput,
} from '../../mesh/feature'
import { createCompactIndexedFaceGeometry, mergeOwnedFaceGeometries } from '../../mesh/utils'
import type { ImportedMeshTopologyBinding } from './ImportedMeshTopologyBinding'

/**
 * 坐标系一致性判断容差。
 */
const LOCAL_MATRIX_EQUALITY_EPSILON = 1e-9

/**
 * `Matrix4` 单位矩阵元素快照。
 */
const IDENTITY_MATRIX_ELEMENTS = [
  1, 0, 0, 0,
  0, 1, 0, 0,
  0, 0, 1, 0,
  0, 0, 0, 1,
] as const

/**
 * 判断两个 Object3D 当前是否共享同一套本地坐标。
 *
 * 设计说明：
 * FeatureFace 代理必须位于 host 本地坐标系。provider 只在 host 与 visible mesh 坐标一致时
 * 复制当前面实际引用的 GLB 顶点；否则回退 BRep，避免把坐标变换隐式塞进代理几何。
 *
 * @param visibleMesh 可见 GLB mesh。
 * @param host FeatureResultMesh host。
 * @param sourceToHostMatrix 可复用的坐标变换矩阵。
 * @returns 是否可直接使用 visible mesh 本地几何数据。
 */
function canUseVisibleMeshLocalGeometry(
  visibleMesh: THREE.Mesh,
  host: THREE.Object3D,
  sourceToHostMatrix: THREE.Matrix4,
): boolean {
  visibleMesh.updateWorldMatrix(true, false)
  host.updateWorldMatrix(true, false)
  sourceToHostMatrix
    .copy(host.matrixWorld)
    .invert()
    .multiply(visibleMesh.matrixWorld)
  const elements = sourceToHostMatrix.elements
  return elements.every((value, index) => (
    Math.abs(value - (IDENTITY_MATRIX_ELEMENTS[index] ?? 0)) <= LOCAL_MATRIX_EQUALITY_EPSILON
  ))
}

/**
 * 从 GLB visible mesh 构建指定范围的 FeatureFace 显示几何。
 *
 * @param input 构建输入。
 * @returns FeatureFace 几何；无法安全复用时返回 `null`。
 */
function createVisibleFaceGeometry(input: {
  visibleMesh: THREE.Mesh
  host: THREE.Object3D
  triangleRanges: [number, number][]
  sourceToHostMatrix: THREE.Matrix4
}): THREE.BufferGeometry | null {
  if (!canUseVisibleMeshLocalGeometry(input.visibleMesh, input.host, input.sourceToHostMatrix)) {
    return null
  }

  const geometries: THREE.BufferGeometry[] = []
  try {
    for (const triangleRange of input.triangleRanges) {
      const geometry = createCompactIndexedFaceGeometry({ sourceGeometry: input.visibleMesh.geometry, triangleRange })
      if (!geometry) {
        geometries.forEach(part => part.dispose())
        return null
      }
      geometries.push(geometry)
    }
  } catch (error) {
    geometries.forEach(geometry => geometry.dispose())
    throw error
  }
  return mergeOwnedFaceGeometries(geometries)
}

/**
 * 导入模型特征面几何 provider。
 *
 * 设计说明：
 * 1. FeatureFace 只有在存在显式 BRep face -> GLB triangleRange 契约时才复用 GLB 可见网格；
 * 2. 缺少显式契约时必须回退 BRep geometry，禁止按 primitive 顺序猜测，避免错面；
 * 3. 使用 GLB 时只复制当前面真实引用的最小顶点集合，禁止共享源 BufferAttribute，避免 WebGPU 管线绑定被代理几何污染。
 */
export class ImportedTopologyFaceGeometryProvider implements TopologyFeatureFaceGeometryProvider {
  /** 复用坐标系校验矩阵，避免 hover 临时面代理路径重复分配。 */
  private readonly sourceToHostMatrix = new THREE.Matrix4()

  /**
   * @param binding 当前显示 Mesh 的正式拓扑绑定。
   */
  public constructor(private readonly binding: ImportedMeshTopologyBinding) {}

  /**
   * 创建指定拓扑面的显示几何。
   *
   * @param input 面代理几何构建输入。
   * @returns 可用于 `FeatureFace` 的几何；无法构建时返回 `null`。
   */
  public createFaceGeometry(input: TopologyFeatureFaceGeometryProviderInput): THREE.BufferGeometry | null {
    const triangleRanges = this.binding.resolveFaceTriangleRanges(input.face)
    if (triangleRanges.length === 0) {
      return null
    }

    return createVisibleFaceGeometry({
      visibleMesh: this.binding.visibleMesh,
      host: input.host,
      triangleRanges,
      sourceToHostMatrix: this.sourceToHostMatrix,
    })
  }
}
